import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { CoundownModal } from './CoundownModal';
import { FeatureOnboardingModal } from './FeatureOnboardingModal';
import { MilestoneScale } from './MilestoneScale';
import { ProIconSvg } from '../../../icons/Pro.icon';
import { primaryColor, encountersToUnlock } from './config';
import { useSelector, useDispatch } from 'react-redux';
import { FronteggContext } from '@frontegg/rest-api';
import { useAuth, useAuthActions } from '@frontegg/react';
import { AxiosAmbient } from 'src/lib';
import { debounce } from 'lodash';
import { UpgradeSuccessModal } from './UpgradeSeccessModal';
import { getCurrentPractitionerEntitlements } from '../../../store/actions/practitioner.action';
import { analytics } from 'src/services';

function ProTrialCountDown({
  mobile = false,
  isMinimized = false,
  getCustomTriggerJsx,
}) {
  const { loadEntitlements } = useAuthActions();
  const { user } = useAuth();
  const { settingsOrgValue } = useSelector((state) => state?.appointmentState);
  const { isEncounterListLoading, encounterList } = useSelector(
    (state) => state?.encounterState
  );
  const dispatch = useDispatch();

  const [completedEncounters, setCompletedEncounters] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [featureOnboardingModal, setFeatureOnboardingModal] = useState(false);
  const [coundownModal, setCoundownModal] = useState(false);
  const [upgradeSuccessModal, setUpgradeSuccessModal] = useState(false);

  const upgradeOrg = useCallback(async () => {
    setLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
      };
      const result = await AxiosAmbient.post(
        '/aura_pro_trial/upgrade',
        {},
        {
          headers,
        }
      );
      if (result.status < 300) {
        setShowCountdown(false);
        setCoundownModal(false);
        setFeatureOnboardingModal(false);
        setUpgradeSuccessModal(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // these are fetched on success modal close to avoid un mountig the component which contains the modal itself
  const refetchEntitlements = useCallback(async () => {
    setLoading(true);
    dispatch(getCurrentPractitionerEntitlements());
    loadEntitlements();
    setLoading(false);
    setUpgradeSuccessModal(false);
  }, [dispatch, loadEntitlements]);

  const fetchCompletedEncounters = useMemo(() => {
    return debounce(async () => {
      setLoading(true);
      const searchFilters = {
        status: 'completed',
      };
      try {
        const headers = {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
        };

        const queryParams = new URLSearchParams(searchFilters).toString();
        const result = await AxiosAmbient.get(`/encounters?${queryParams}`, {
          headers,
        });
        const completedCount = result.data.encounters?.length;
        if (completedCount >= encountersToUnlock) {
          upgradeOrg();
          setCompletedEncounters(encountersToUnlock);
        } else {
          setCompletedEncounters(completedCount);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }, 1000);
  }, [upgradeOrg]);

  const showCountdownIfEntitled = async () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${FronteggContext.getAccessToken()}`,
    };

    const result = await AxiosAmbient.get('/aura_pro_trial/entitlement', {
      headers,
    });
    if (result.status < 300 && result.data.entitled) {
      setShowCountdown(true);
    }
  };

  const visibleCompletedEncounters = useMemo(() => {
    return (encounterList || []).filter(
      (encounter) => encounter.in_visit_status === 'completed'
    ).length;
  }, [encounterList]);

  useEffect(() => {
    // Fetch encounters when encounter list stops loading
    if (!isEncounterListLoading && showCountdown) {
      fetchCompletedEncounters();
    }
  }, [
    isEncounterListLoading,
    fetchCompletedEncounters,
    showCountdown,
    visibleCompletedEncounters,
  ]);

  const lastOnboardingDemoValueRef = useRef(settingsOrgValue?.onboarding_demo);
  useEffect(() => {
    // check if user is onboarded
    if (settingsOrgValue?.onboarding_demo) {
      // if user just onboarded, show feature onboarding modal
      if (
        lastOnboardingDemoValueRef.current !== settingsOrgValue?.onboarding_demo
      ) {
        setTimeout(() => {
          setFeatureOnboardingModal(true);
        }, 120000); // 2 minutes
      } else {
        // user is already onboarded show countdown
        showCountdownIfEntitled();
      }
    }
    lastOnboardingDemoValueRef.current = settingsOrgValue?.onboarding_demo;
  }, [settingsOrgValue?.onboarding_demo]);

  const triggerCountdown = () => {
    // eslint-disable-next-line no-undef
    analytics.track('Clicked Pro Trial Countdown Button', {
      mobile,
      email: user?.email,
    });
    setCoundownModal(true);
  };

  const getTriggerJsx = () => {
    if (getCustomTriggerJsx) {
      return getCustomTriggerJsx({
        onClick: triggerCountdown,
        label: 'Pro Trial Countdown',
      });
    }

    if (mobile || isMinimized) {
      return (
        <div
          onClick={triggerCountdown}
          className={`text-white cursor-pointer flex items-center p-3 rounded-full shadow-xl bg-[${primaryColor}]`}
        >
          <ProIconSvg size={20} fill="white" />
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center bg-[#0F9F67] rounded-2xl p-4 text-white max-w-xs">
        <div className="bg-white rounded-full p-2 mb-3">
          <ProIconSvg size={20} fill={primaryColor} />
        </div>
        <h2 className="text-sm font-medium mb-2">Countdown to Pro Trial</h2>
        <MilestoneScale
          total={encountersToUnlock}
          completed={completedEncounters}
          inverse
        />
        <div className="flex flex-col items-center mb-4 text-sm">
          <div className="font-semibold">
            {encountersToUnlock - completedEncounters} more encounters
          </div>
          <div className="text-center">to activate free 30-day pro trial</div>
        </div>
        <button
          className="ih-button bg-white text-[#0F9F67] w-full justify-center"
          onClick={triggerCountdown}
          disabled={loading}
        >
          {loading ? <LoadingOutlined /> : 'Start Pro'}
        </button>
      </div>
    );
  };

  return (
    <>
      {showCountdown && getTriggerJsx()}
      {coundownModal && (
        <CoundownModal
          open
          onCancel={() => setCoundownModal(false)}
          completedEncounters={completedEncounters}
        />
      )}
      {featureOnboardingModal && (
        <FeatureOnboardingModal
          open
          onCancel={() => {
            setFeatureOnboardingModal(false);
            showCountdownIfEntitled();
          }}
        />
      )}
      {upgradeSuccessModal && (
        <UpgradeSuccessModal open onCancel={refetchEntitlements} />
      )}
    </>
  );
}

export { ProTrialCountDown };
